<template>
    <v-card class="rounded_card impact__card">
        <v-card-text>
            <div class="impact__head">
                <h3 class="mb-0">Impact over time</h3>
                <span class="impact__muted ml-3" :title="result.url">{{ shorten(result.url, 70) }}</span>
            </div>
            <p class="impact__muted mt-1 mb-0">Every audit of this page next to the AI answers that cited it. Citations are counted in the linked AI project, per week.</p>

            <div v-if="loading" class="impact__loading">
                <v-progress-circular indeterminate color="#009f89" size="24"></v-progress-circular>
                <span class="impact__muted">Loading history …</span>
            </div>
            <p v-else-if="error" class="impact__error">{{ error }}</p>
            <template v-else-if="impact">
                <p v-if="!hasProject" class="impact__note">This run is not linked to an AI project, so there is no citation data — only the audit history is shown.</p>

                <!-- weekly citations -->
                <template v-if="hasProject && weekly.length">
                    <div class="impact__chartHead">
                        <span class="impact__eyebrow mb-0">Answers citing this page per week</span>
                        <span class="impact__legend"> <span class="impact__legendbar"></span> citing answers <span class="impact__legenddot"></span> audit </span>
                    </div>
                    <!-- hover any week for its exact numbers; the tooltip follows the hovered bar -->
                    <div class="impact__chart" @mouseleave="hoverIndex = null">
                        <span class="impact__ymax">max {{ maxWeekly }}/week</span>
                        <div class="impact__bars" :style="{ gridTemplateColumns: 'repeat(' + weekly.length + ', minmax(0, 1fr))' }">
                            <div
                                v-for="(week, index) in weekly"
                                :key="week.week_start"
                                class="impact__barcol"
                                :class="{ 'impact__barcol--hover': hoverIndex === index, 'impact__barcol--audit': week.audits.length > 0 }"
                                @mouseenter="hoverIndex = index">
                                <span v-if="hoverIndex === index" class="impact__barvalue">{{ week.responses }}</span>
                                <div class="impact__bar" :style="{ height: barHeight(week.responses) + '%' }"></div>
                                <span v-if="week.audits.length" class="impact__marker"></span>
                            </div>
                        </div>
                        <div v-if="hoveredWeek" class="impact__tooltip" :class="tooltipAlignClass" :style="{ left: tooltipLeft }">
                            <span class="impact__tooltiptitle">{{ hoveredWeek.label }}</span>
                            <span class="impact__tooltipline"
                                ><strong>{{ hoveredWeek.responses }}</strong> {{ hoveredWeek.citing }}</span
                            >
                            <span v-for="(audit, i) in hoveredWeek.auditRows" :key="'ta-' + i" class="impact__tooltipline impact__tooltipline--audit">{{ audit.label }}</span>
                        </div>
                    </div>
                    <div class="impact__axis">
                        <span>{{ weekly[0].week_start }}</span>
                        <span>{{ impact.total_citations }} citing answers in total</span>
                        <span>{{ weekly[weekly.length - 1].week_start }}</span>
                    </div>
                </template>
                <p v-else-if="hasProject" class="impact__note">No AI answer in this project has cited this page in the last {{ days }} days yet.</p>

                <!-- audits -->
                <span class="impact__eyebrow">Audits of this page</span>
                <div v-if="!impact.audits.length" class="impact__muted">No finished audit yet.</div>
                <div v-for="audit in auditsNewestFirst" :key="String(audit.result_id || audit.fetched_at)" class="impact__audit">
                    <div class="impact__auditmain">
                        <span class="impact__date">{{ formatDate(audit.fetched_at) }}</span>
                        <span class="impact__score" :style="{ color: scoreColor(audit.score) }">{{ audit.score }}</span>
                        <span v-if="audit.score_delta !== null" class="impact__delta" :class="audit.score_delta > 0 ? 'impact__delta--up' : audit.score_delta < 0 ? 'impact__delta--down' : ''">
                            {{ audit.score_delta > 0 ? "+" : "" }}{{ audit.score_delta }}
                        </span>
                        <span v-if="audit.result_id && String(audit.result_id) === String(result._id)" class="impact__pill">this audit</span>
                    </div>
                    <div v-if="hasProject" class="impact__auditcites">
                        <template v-if="audit.maturity === 'none'">Too fresh — citation data for the {{ impact.window_days }} days after this audit is not in yet.</template>
                        <template v-else>
                            {{ audit.before_per_week }} citing answers/week before → <strong>{{ audit.after_per_week }}</strong> after
                            <span v-if="audit.change_pct !== null" :class="audit.change_pct > 0 ? 'impact__delta--up' : audit.change_pct < 0 ? 'impact__delta--down' : ''"
                                >({{ audit.change_pct > 0 ? "+" : "" }}{{ audit.change_pct }} %)</span
                            >
                            <span v-if="audit.maturity === 'early'" class="impact__muted">· only {{ audit.after_days }} of {{ impact.window_days }} days of data yet</span>
                        </template>
                    </div>
                </div>

                <p class="impact__note mb-0">
                    "Before" and "after" compare {{ impact.window_days }}-day windows around each audit. Perplexity and AI Overviews react within days, ChatGPT much more slowly — treat this as
                    evidence, not proof.
                </p>
            </template>
        </v-card-text>
    </v-card>
</template>

<script>
    import { fetchImpact } from "./seoAuditApi"

    export default {
        name: "ImpactCard",
        props: {
            run: { type: Object, required: true },
            result: { type: Object, required: true },
            companyId: { type: String, required: true }
        },
        data() {
            return {
                loading: false,
                error: "",
                impact: null,
                hasProject: false,
                days: 120,
                // index of the hovered week bar (null = nothing hovered) — drives the tooltip
                hoverIndex: null,
                isDestroyed: false
            }
        },
        computed: {
            auditsNewestFirst() {
                return this.impact ? this.impact.audits.slice().reverse() : []
            },
            maxWeekly() {
                return this.impact ? Math.max(1, ...this.impact.weekly.map((w) => w.responses)) : 1
            },
            // weeks with the audits that fall into them (for the markers under the bars and the tooltip)
            weekly() {
                if (!this.impact) return []
                const audits = this.impact.audits
                return this.impact.weekly.map((week) => {
                    const start = new Date(week.week_start)
                    const end = new Date(start.getTime() + 7 * 24 * 3600 * 1000)
                    const inWeek = audits.filter((a) => {
                        const d = new Date(a.fetched_at)
                        return d >= start && d < end
                    })
                    return {
                        ...week,
                        label: this.formatDate(start) + " – " + this.formatDate(new Date(end.getTime() - 1)),
                        citing: week.responses === 1 ? "citing answer" : "citing answers",
                        audits: inWeek.map((a) => this.formatDate(a.fetched_at) + " (" + a.score + ")"),
                        auditRows: inWeek.map((a) => ({ label: "Audit " + this.formatDate(a.fetched_at) + " · score " + a.score }))
                    }
                })
            },
            hoveredWeek() {
                return this.hoverIndex !== null && this.weekly[this.hoverIndex] ? this.weekly[this.hoverIndex] : null
            },
            // the tooltip sits above the hovered bar; near the edges it hangs to one side so it stays in the card
            tooltipLeft() {
                if (this.hoverIndex === null || !this.weekly.length) return "0%"
                return ((this.hoverIndex + 0.5) / this.weekly.length) * 100 + "%"
            },
            tooltipAlignClass() {
                if (this.hoverIndex === null || !this.weekly.length) return ""
                const position = (this.hoverIndex + 0.5) / this.weekly.length
                if (position < 0.18) return "impact__tooltip--left"
                if (position > 0.82) return "impact__tooltip--right"
                return ""
            }
        },
        watch: {
            "result._id": {
                immediate: true,
                handler() {
                    this.load()
                }
            }
        },
        beforeDestroy() {
            this.isDestroyed = true
        },
        methods: {
            shorten(value, max) {
                const text = String(value || "")
                return text.length > max ? text.slice(0, max - 1) + "…" : text
            },
            formatDate(value) {
                if (!value) return "-"
                const d = new Date(value)
                return isNaN(d) ? "-" : d.toISOString().slice(0, 10)
            },
            scoreColor(score) {
                if (score === null || score === undefined) return "#5a8c84"
                if (score >= 80) return "#009f89"
                if (score >= 60) return "#e5a400"
                return "#d94040"
            },
            barHeight(value) {
                return Math.max(value ? 6 : 2, Math.round((value / this.maxWeekly) * 100))
            },
            async load() {
                if (!this.run || !this.result || !this.result._id) return
                this.loading = true
                this.error = ""
                try {
                    const data = await fetchImpact(this.companyId, { runId: String(this.run._id), resultId: String(this.result._id), days: this.days })
                    if (this.isDestroyed) return
                    this.impact = data && data.impact ? data.impact : null
                    this.hasProject = !!(data && data.has_project)
                    if (!this.impact) this.error = "The history could not be loaded."
                } catch (e) {
                    if (!this.isDestroyed) this.error = "The history could not be loaded."
                } finally {
                    if (!this.isDestroyed) this.loading = false
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .impact__card {
        box-shadow: 0 2px 18px rgba(0, 0, 0, 0.06) !important;
    }
    .impact__head {
        display: flex;
        align-items: baseline;
        min-width: 0;
    }
    .impact__eyebrow {
        display: block;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #5a8c84;
        margin-top: 18px;
        margin-bottom: 8px;
    }
    .impact__muted {
        font-size: 13px;
        color: #5a8c84;
    }
    .impact__note {
        font-size: 12px;
        color: #5a8c84;
        margin-top: 12px;
    }
    .impact__error {
        margin-top: 12px;
        color: #d94040;
        font-size: 13px;
    }
    .impact__loading {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 16px;
    }
    .impact__chartHead {
        display: flex;
        align-items: baseline;
        gap: 12px;
        margin-top: 18px;
        margin-bottom: 8px;
    }
    .impact__chartHead .impact__eyebrow {
        margin-top: 0;
    }
    .impact__legend {
        margin-left: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: #5a8c84;
        white-space: nowrap;
    }
    .impact__legendbar {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        background: #9fd9cf;
    }
    .impact__legenddot {
        display: inline-block;
        width: 8px;
        height: 8px;
        margin-left: 8px;
        border-radius: 50%;
        background: #009f89;
    }
    .impact__chart {
        position: relative;
        padding-top: 14px;
    }
    .impact__ymax {
        position: absolute;
        top: 0;
        right: 0;
        font-size: 10px;
        color: #5a8c84;
    }
    .impact__bars {
        display: grid;
        align-items: end;
        gap: 3px;
        height: 110px;
        padding-bottom: 10px;
        border-bottom: 1px solid #eaf3f1;
        background: linear-gradient(to top, transparent 10px, #f1f6f5 10px, #f1f6f5 11px, transparent 11px);
    }
    .impact__barcol {
        position: relative;
        height: 100%;
        display: flex;
        align-items: flex-end;
        cursor: default;
        border-radius: 3px 3px 0 0;
    }
    .impact__barcol--audit {
        background: rgba(0, 159, 137, 0.06);
    }
    .impact__barcol--hover {
        background: rgba(0, 159, 137, 0.12);
    }
    .impact__bar {
        width: 100%;
        background: #9fd9cf;
        border-radius: 3px 3px 0 0;
        transition: background 120ms ease;
    }
    .impact__barcol--hover .impact__bar {
        background: #009f89;
    }
    .impact__barvalue {
        position: absolute;
        left: 50%;
        bottom: 100%;
        transform: translateX(-50%);
        font-size: 11px;
        font-weight: 700;
        color: #009f89;
        line-height: 1;
        padding-bottom: 2px;
        pointer-events: none;
    }
    .impact__tooltip {
        position: absolute;
        top: 0;
        transform: translate(-50%, -100%);
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 8px 10px;
        border-radius: 8px;
        background: #0d2622;
        color: #ffffff;
        font-size: 12px;
        white-space: nowrap;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
        pointer-events: none;
        z-index: 2;
    }
    .impact__tooltip--left {
        transform: translate(0, -100%);
    }
    .impact__tooltip--right {
        transform: translate(-100%, -100%);
    }
    .impact__tooltiptitle {
        font-weight: 700;
        color: #9fd9cf;
    }
    .impact__tooltipline strong {
        color: #1cedb3;
    }
    .impact__tooltipline--audit {
        color: rgba(255, 255, 255, 0.8);
    }
    .impact__marker {
        position: absolute;
        bottom: -8px;
        left: 50%;
        width: 8px;
        height: 8px;
        margin-left: -4px;
        border-radius: 50%;
        background: #009f89;
        border: 2px solid #ffffff;
    }
    .impact__axis {
        display: flex;
        justify-content: space-between;
        font-size: 11px;
        color: #5a8c84;
        margin-top: 10px;
    }
    .impact__audit {
        padding: 8px 0;
        border-bottom: 1px solid #f1f6f5;
    }
    .impact__auditmain {
        display: flex;
        align-items: baseline;
        gap: 10px;
    }
    .impact__date {
        font-size: 13px;
        min-width: 86px;
    }
    .impact__score {
        font-size: 18px;
        font-weight: 700;
    }
    .impact__delta {
        font-size: 12px;
        font-weight: 600;
        color: #5a8c84;
    }
    .impact__delta--up {
        color: #009f89;
    }
    .impact__delta--down {
        color: #d94040;
    }
    .impact__pill {
        font-size: 11px;
        font-weight: 600;
        border-radius: 999px;
        padding: 2px 8px;
        background: #e0f5f2;
        color: #009f89;
    }
    .impact__auditcites {
        font-size: 13px;
        margin-top: 4px;
    }
</style>
